import {
    ChangeDetectorRef,
    Component,
    ComponentFactoryResolver,
    ComponentRef,
    EmbeddedViewRef,
    HostBinding,
    Injector,
    Input,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';

import { ElPosition } from './overlay-position';
import { ElComponentPortal, ElPortalInjector, ElPortalOutletDirective, ElTemplatePortal } from './mapping';

export interface ElRenderableContainer {

    /**
     * A renderContent method renders content with provided context.
     * Naturally, this job has to be done by ngOnChanges lifecycle hook, but
     * ngOnChanges hook will be triggered only if we update content or context properties
     * through template property binding syntax. But in our case we're updating these properties programmatically.
     * */
    renderContent();
}

export abstract class ElPositionedContainer {
    @Input() position: ElPosition;

    @HostBinding('class.el-overlay-top')
    get top(): boolean {
        return this.position === ElPosition.TOP;
    }

    @HostBinding('class.el-overlay-top-start')
    get topStart(): boolean {
        return this.position === ElPosition.TOP_START;
    }

    @HostBinding('class.el-overlay-top-end')
    get topEnd(): boolean {
        return this.position === ElPosition.TOP_END;
    }

    @HostBinding('class.el-overlay-right')
    get right(): boolean {
        return this.position === ElPosition.RIGHT || this.position === ElPosition.END;
    }

    @HostBinding('class.el-overlay-end-top')
    get endTop(): boolean {
        return this.position === ElPosition.END_TOP;
    }

    @HostBinding('class.el-overlay-end-bottom')
    get endBottom(): boolean {
        return this.position === ElPosition.END_BOTTOM;
    }

    @HostBinding('class.el-overlay-bottom')
    get bottom(): boolean {
        return this.position === ElPosition.BOTTOM;
    }

    @HostBinding('class.el-overlay-bottom-start')
    get bottomStart(): boolean {
        return this.position === ElPosition.BOTTOM_START;
    }

    @HostBinding('class.el-overlay-bottom-end')
    get bottomEnd(): boolean {
        return this.position === ElPosition.BOTTOM_END;
    }

    @HostBinding('class.el-overlay-left')
    get left(): boolean {
        return this.position === ElPosition.LEFT || this.position === ElPosition.START;
    }

    @HostBinding('class.el-overlay-start-top')
    get startTop(): boolean {
        return this.position === ElPosition.START_TOP;
    }

    @HostBinding('class.el-overlay-start-bottom')
    get startBottom(): boolean {
        return this.position === ElPosition.START_BOTTOM;
    }
}


@Component({
    selector: 'el-overlay-container',
    template: `
    <div *ngIf="isStringContent" class="primitive-overlay">{{ content }}</div>
    <ng-template elPortalOutlet></ng-template>
  `,
})
export class ElOverlayContainerComponent {

    // TODO static must be false as of Angular 9.0.0, issues/1514
    @ViewChild(ElPortalOutletDirective, { static: true }) portalOutlet: ElPortalOutletDirective;

    isAttached: boolean = false;

    content: string;

    constructor(protected vcr: ViewContainerRef,
        protected injector: Injector, private changeDetectorRef: ChangeDetectorRef) {
    }

    get isStringContent(): boolean {
        return !!this.content;
    }

    attachComponentPortal<T>(portal: ElComponentPortal<T>, context?: Object): ComponentRef<T> {
        portal.injector = this.createChildInjector(portal.componentFactoryResolver);
        const componentRef = this.portalOutlet.attachComponentPortal(portal);
        if (context) {
            Object.assign(componentRef.instance, context);
        }
        componentRef.changeDetectorRef.markForCheck();
        componentRef.changeDetectorRef.detectChanges();
        this.isAttached = true;
        return componentRef;
    }

    attachTemplatePortal<C>(portal: ElTemplatePortal<C>): EmbeddedViewRef<C> {
        const templateRef = this.portalOutlet.attachTemplatePortal(portal);
        templateRef.detectChanges();
        this.isAttached = true;
        return templateRef;
    }

    attachStringContent(content: string) {
        this.content = content;
        this.changeDetectorRef.markForCheck();
        this.changeDetectorRef.detectChanges();
        this.isAttached = true;
    }

    detach() {
        if (this.portalOutlet.hasAttached()) {
            this.portalOutlet.detach();
        }
        this.attachStringContent(null);
        this.isAttached = false
    }

    protected createChildInjector(cfr: ComponentFactoryResolver): ElPortalInjector {
        return new ElPortalInjector(this.injector, new WeakMap([
            [ComponentFactoryResolver, cfr],
        ]));
    }
}
